import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, ShieldQuestion, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Avatar, severityColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

export default function Families() {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [drafting, setDrafting] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = () => api.get("/family/threads").then((r) => {
    setThreads(r.data);
    if (!active && r.data.length) setActive(r.data[0]);
  }).catch(() => setThreads([]));
  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (active) api.get(`/family/threads/${active.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [active]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    if (!input.trim() || !active) return;
    const text = input; setInput("");
    try {
      const { data } = await api.post(`/family/threads/${active.id}/messages`, { content: text });
      setMessages((m) => [...m, data]);
    } catch (e) { toast.error("Send failed"); }
  };

  const draft = async () => {
    if (!active) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/ai/family-summary/${active.resident_id}`);
      setInput(data.draft);
      toast.info("AI drafted a daily update — review before sending", { duration: 4000 });
    } catch (e) { toast.error("AI draft failed"); } finally { setDrafting(false); }
  };

  if (!threads) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  return (
    <div>
      <PageHeader overline="Care · Family Engagement" title="Families"
        description="Secure family messaging with AI daily-summary drafting. Clinically or legally sensitive messages are always staff-reviewed before sending." />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12" style={{ height: "calc(100vh - 230px)" }}>
        {/* Threads */}
        <div className="lg:col-span-4 rounded-xl border bg-card shadow-sm overflow-hidden flex flex-col">
          <div className="border-b px-4 py-3 text-sm font-bold">Conversations</div>
          <div className="careiq-scroll flex-1 divide-y overflow-y-auto">
            {threads.map((t) => (
              <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => setActive(t)}
                className={cn("flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                  active?.id === t.id && "bg-muted/60")}>
                <Avatar name={t.guardian} seed={t.resident_name.length} size={38} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{t.guardian}</div>
                  <div className="truncate text-xs text-muted-foreground">re: {t.resident_name}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] font-medium" style={{ color: severityColor(t.sentiment === "Concerned" ? "Medium" : "Low") }}>{t.sentiment}</span>
                  {t.unread > 0 && <span className="h-2 w-2 rounded-full bg-[#155EEF]" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className="lg:col-span-8 rounded-xl border bg-card shadow-sm flex flex-col overflow-hidden">
          {active ? (
            <>
              <div className="flex items-center gap-3 border-b px-4 py-3">
                <Avatar name={active.guardian} seed={active.resident_name.length} size={36} />
                <div><div className="text-sm font-bold">{active.guardian}</div>
                  <div className="text-xs text-muted-foreground">Guardian of {active.resident_name} · {active.guardian_phone}</div></div>
              </div>
              <div ref={scrollRef} className="careiq-scroll flex-1 space-y-3 overflow-y-auto p-4">
                {messages.map((m) => (
                  <div key={m.id} className={cn("flex", m.sender === "staff" ? "justify-end" : "justify-start")}>
                    <div className={cn("max-w-[75%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed",
                      m.sender === "staff" ? "bg-[#155EEF] text-white" : "border bg-background")}>
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-70">{m.sender_name}</div>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t p-3">
                <div className="mb-2 flex items-center gap-2">
                  <button data-testid="family-ai-draft-btn" onClick={draft} disabled={drafting}
                    className="flex items-center gap-1.5 rounded-md bg-[#7C3AED] px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-60">
                    {drafting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} AI Daily Summary
                  </button>
                  <span className="flex items-center gap-1 text-[11px] text-[#F79009]"><ShieldQuestion size={12} /> Review AI drafts before sending</span>
                </div>
                <div className="flex items-end gap-2 rounded-lg border bg-background p-2 focus-within:border-[#155EEF]">
                  <textarea data-testid="family-input" rows={2} value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Write a message to the family…" className="max-h-32 flex-1 resize-none bg-transparent px-1 text-sm outline-none" />
                  <button data-testid="family-send-btn" onClick={send} disabled={!input.trim()}
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-[#155EEF] text-white transition-opacity disabled:opacity-40"><Send size={15} /></button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-muted-foreground"><MessageSquare className="mr-2" size={18} /> Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
